/**
 * AuthContext.js - Authentication Context Provider
 *
 * This file implements React's Context API to manage authentication state across the application.
 * It provides a centralized way to handle user authentication status and related functions.
 *
 * Key Features:
 * - Manages global authentication state
 * - Handles login/logout functionality
 * - Persists authentication state using cookies
 * - Provides a custom hook for easy access to auth state
 *
 * @module AuthContext
 */

import React, { createContext, useState, useContext, useCallback } from "react";
import Cookies from "js-cookie";

// Create the authentication context
const AuthContext = createContext(null);

/**
 * AuthProvider Component
 *
 * Wraps the application to provide authentication context to all child components.
 * Manages the authentication state and provides login/logout functionality.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that need access to auth context
 * @returns {JSX.Element} AuthProvider component
 */
export const AuthProvider = ({ children }) => {
  // Sync with cookie on first paint so routes like kiosk settings don’t flash
  // “logged out” before a mount effect runs.
  const [isLoggedIn, setIsLoggedIn] = useState(
    () => !!Cookies.get("accessToken")
  );

  const login = useCallback(() => setIsLoggedIn(true), []);

  const logout = useCallback(() => {
    Cookies.remove("accessToken");
    setIsLoggedIn(false);
  }, []);

  return (
    <AuthContext.Provider value={{ isLoggedIn, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to access authentication context
 *
 * @returns {Object} Authentication context containing isLoggedIn, login, and logout
 */
export const useAuth = () => useContext(AuthContext);
